/**
 * Click-to-insert caret math for the invoice-message-template editor
 * (the "Mẫu tin nhắn gửi hóa đơn" card in BillingRulesSection) — see
 * docs/superpowers/specs/2026-08-17-manual-send-template-design.md §3.
 * Pure and DOM-free on purpose: the component reads `selectionStart`/`End`
 * off the textarea ref, hands them here, and applies the result — this file
 * never touches the DOM itself, so it's trivially testable.
 */

/** A textarea's caret (`start === end`) or selection (`start < end`). */
export interface TextSelection {
  start: number;
  end: number;
}

export interface InsertAtCursorResult {
  /** `text` with `insertion` spliced in, replacing the selection (or the
   *  bare caret, when `start === end`). */
  text: string;
  /** Where the caret should land after the insertion — right after the
   *  inserted text, so typing continues naturally. */
  cursor: number;
}

/**
 * Splice `insertion` into `text` at `sel`, replacing any selected range.
 * Clamps `sel` to `text`'s bounds and tolerates `start > end` (a caller
 * passing a reversed selection) — never throws on out-of-range input.
 */
export function insertAtCursor(text: string, sel: TextSelection, insertion: string): InsertAtCursorResult {
  const lo = Math.max(0, Math.min(sel.start, sel.end));
  const hi = Math.min(text.length, Math.max(sel.start, sel.end));
  const nextText = text.slice(0, lo) + insertion + text.slice(hi);
  return { text: nextText, cursor: lo + insertion.length };
}
